import { z } from "zod";

import {
	AiDraftItem,
	CopywritingChannel,
	GenerateCopywritingResponse,
	ToneOfVoiceCapturePayload,
	ToneOfVoiceChannel,
	ToneOfVoiceLeadContext,
	ToneOfVoiceMessageType,
} from "./types";

const STEP_KEYS = ["step_1", "step_2", "step_3"] as const;
const STEP_TIMING_DAYS = [0, 3, 7];

// ── Narrow Zod schemas for safe parsing of upstream context payloads ──

const ContextPayloadSchema = z
	.object({
		lead: z
			.object({
				persona_category: z.string().optional(),
				company_category: z.string().optional(),
			})
			.partial()
			.optional(),
		signal: z
			.object({
				triggering_signal: z
					.object({
						type: z.string().optional(),
					})
					.partial()
					.nullable()
					.optional(),
			})
			.partial()
			.optional(),
		generation_config: z
			.object({
				language: z.string().optional(),
			})
			.partial()
			.optional(),
	})
	.partial();

const MessagingStrategySchema = z
	.object({
		step_1: z.object({ framework_name: z.string().optional() }).partial().optional(),
		step_2: z.object({ framework_name: z.string().optional() }).partial().optional(),
		step_3: z.object({ framework_name: z.string().optional() }).partial().optional(),
	})
	.partial();

// ── Message type derivation per BE ToneOfVoiceMessageType table ──

const linkedinMessageType = (stepIndex: number, isInviteNote: boolean): ToneOfVoiceMessageType => {
	if (stepIndex === 0 && isInviteNote) {
		return ToneOfVoiceMessageType.LI_CONNECTION_REQUEST;
	}

	if (stepIndex === 0) {
		return ToneOfVoiceMessageType.LI_INTRODUCTION;
	}

	if (stepIndex === 2) {
		return ToneOfVoiceMessageType.LI_BREAKUP;
	}

	return ToneOfVoiceMessageType.LI_FOLLOW_UP;
};

const emailMessageType = (stepIndex: number): ToneOfVoiceMessageType => {
	if (stepIndex === 0) {
		return ToneOfVoiceMessageType.EMAIL_INTRODUCTION;
	}

	if (stepIndex === 2) {
		return ToneOfVoiceMessageType.EMAIL_BREAKUP;
	}

	return ToneOfVoiceMessageType.EMAIL_FOLLOW_UP;
};

const buildAiOrigin = (
	originalText: string,
	messageType: ToneOfVoiceMessageType,
	channel: ToneOfVoiceChannel,
	language: string | undefined,
	frameworkUsed: string | undefined,
	leadContext: ToneOfVoiceLeadContext | undefined
): ToneOfVoiceCapturePayload => {
	const payload: ToneOfVoiceCapturePayload = { originalText, messageType, channel };

	if (language) {
		payload.language = language;
	}

	if (frameworkUsed) {
		payload.frameworkUsed = frameworkUsed;
	}

	if (leadContext && Object.keys(leadContext).length > 0) {
		payload.leadContext = leadContext;
	}

	return payload;
};

/**
 * Convert a full_sequence copywriting result to AiDraftItem[] according to the
 * requested channel. Returns an empty array for non full_sequence results or
 * when the sequence output is missing.
 *
 * When `contextPayload` and/or `messagingStrategy` are passed, each draft item
 * is enriched with a tone-of-voice `aiOrigin` snapshot used downstream to
 * piggyback edit-diff capture on the send-messages call.
 */
export const sequenceToDraftItems = (
	result: GenerateCopywritingResponse | null | undefined,
	channel: CopywritingChannel,
	contextPayload?: unknown,
	messagingStrategy?: unknown
): AiDraftItem[] => {
	if (!result || result.type !== "full_sequence") {
		return [];
	}

	const sequence = result.sequenceOutput?.sequence;

	if (!sequence) {
		return [];
	}

	// Only emit aiOrigin metadata when the caller provided generation context,
	// so callers that just want plain drafts (and existing tests) keep their
	// previous behaviour.
	const captureEnabled = contextPayload !== undefined || messagingStrategy !== undefined;

	const parsedContext = ContextPayloadSchema.safeParse(contextPayload);
	const parsedStrategy = MessagingStrategySchema.safeParse(messagingStrategy);

	const language = parsedContext.success
		? parsedContext.data.generation_config?.language
		: undefined;

	const leadContext: ToneOfVoiceLeadContext = {};

	if (parsedContext.success) {
		const { lead, signal } = parsedContext.data;

		if (lead?.persona_category) {
			leadContext.personaCategory = lead.persona_category;
		}

		if (lead?.company_category) {
			leadContext.companyCategory = lead.company_category;
		}

		if (signal?.triggering_signal?.type) {
			leadContext.triggerType = signal.triggering_signal.type;
		}
	}

	const frameworkForStep = (stepIndex: number): string | undefined => {
		if (!parsedStrategy.success) {
			return undefined;
		}

		const stepKey = STEP_KEYS[stepIndex];

		return parsedStrategy.data[stepKey]?.framework_name;
	};

	const draftItems: AiDraftItem[] = [];

	for (let i = 0; i < STEP_KEYS.length; i++) {
		const step = sequence[STEP_KEYS[i]];

		if (!step) {
			continue;
		}

		const frameworkUsed = frameworkForStep(i);

		if (channel === CopywritingChannel.LINKEDIN || channel === CopywritingChannel.BOTH) {
			// Step 1 can have both an invite note AND a regular message
			if (i === 0 && step.linkedin_connection_request_note) {
				const item: AiDraftItem = {
					content: step.linkedin_connection_request_note,
					channel: "linkedin",
					scheduledInDays: STEP_TIMING_DAYS[i],
					isInviteNote: true,
				};

				if (captureEnabled) {
					item.aiOrigin = buildAiOrigin(
						step.linkedin_connection_request_note,
						linkedinMessageType(i, true),
						ToneOfVoiceChannel.LINKEDIN,
						language,
						frameworkUsed,
						leadContext
					);
				}

				draftItems.push(item);
			}

			if (step.linkedin_message) {
				const item: AiDraftItem = {
					content: step.linkedin_message,
					channel: "linkedin",
					scheduledInDays: STEP_TIMING_DAYS[i],
					isInviteNote: false,
				};

				if (captureEnabled) {
					item.aiOrigin = buildAiOrigin(
						step.linkedin_message,
						linkedinMessageType(i, false),
						ToneOfVoiceChannel.LINKEDIN,
						language,
						frameworkUsed,
						leadContext
					);
				}

				draftItems.push(item);
			}
		}

		if (channel === CopywritingChannel.EMAIL || channel === CopywritingChannel.BOTH) {
			if (step.email?.body) {
				const item: AiDraftItem = {
					content: step.email.body,
					subject: step.email.subject_line,
					channel: "email",
					scheduledInDays: STEP_TIMING_DAYS[i],
				};

				if (captureEnabled) {
					item.aiOrigin = buildAiOrigin(
						step.email.body,
						emailMessageType(i),
						ToneOfVoiceChannel.EMAIL,
						language,
						frameworkUsed,
						leadContext
					);
				}

				draftItems.push(item);
			}
		}
	}

	return draftItems;
};
